var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __classPrivateFieldGet = (this && this.__classPrivateFieldGet) || function (receiver, state, kind, f) {
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a getter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot read private member from an object whose class did not declare it");
    return kind === "m" ? f : kind === "a" ? f.call(receiver) : f ? f.value : state.get(receiver);
};
var _HznAlert_instances, _HznAlert_emit, _HznAlert_handleClick, _HznAlert_handleDismiss, _HznAlert_renderIconSlot, _HznAlert_renderAction;
import { LitElement } from '@horizon/base';
import { nothing, html } from '@horizon/base/html.js';
import { property, query } from '@horizon/base/decorators.js';
import { classMap } from '@horizon/base/directives';
import { ScopedElementsMixin } from '@open-wc/scoped-elements';
import { eventEmitter } from '@horizon/common/events';
import { HasSlotController } from '@horizon/common/controllers';
import { HznStack } from '@horizon/stack';
import { HznText } from '@horizon/text';
import { HznIconWarning, HznIconInfoOutline, HznIconCheck, HznIconClose, HznIconDanger, } from '@horizon/icons/individual';
import AlertStyles from './alert.css.js';
/**
 *
 * @tag hzn-alert
 * @tagname hzn-alert
 * @summary A messaging component for displaying page or section level information and errors
 *
 * @fires {CustomEvent<{}>} action - Emitted when an alert action is present and clicked
 * @fires {CustomEvent<{}>} dismiss - Preventable. Emitted when an alert's dismiss button is present and clicked. When not prevented, automatically sets the `show` property to false. When prevented, does not automatically set the `show` property
 */
export class HznAlert extends ScopedElementsMixin(LitElement) {
    constructor() {
        super(...arguments);
        _HznAlert_instances.add(this);
        /**
         * Sets the show for alert
         */
        this.show = false;
        /**
         * Sets the tone for alert
         * @playroomValues {'critical' | 'caution' | 'info' | 'neutral' | 'positive' | 'flagged'}
         */
        this.tone = 'info';
        /**
         * When true, elevated for alert
         */
        this.elevated = false;
        /**
         * When true, dismissible for alert
         */
        this.dismissible = false;
        /**
         * Sets the heading for alert
         */
        this.heading = '';
        /**
         * Sets the sub-heading for alert
         */
        this.subHeading = '';
        /**
         * Sets the display text of the action
         */
        this.actionText = '';
        /**
         * Set the tag for the action element according to what will happen when it is clicked
         * @playroomValues {'button' | 'a'}
         */
        this.actionElement = 'button';
        this.hasSlotController = new HasSlotController(this, '[default]', 'leading-icon');
        /**
         * @private
         */
        _HznAlert_emit.set(this, eventEmitter(this));
    }
    /**
     * Programmatically dismiss the alert
     */
    dismiss() {
        if (this.innerDismissButton) {
            this.innerDismissButton.click();
        }
    }
    /**
     * Programmatically take the action if it exists
     */
    takeAction() {
        if (this.innerActionButton) {
            this.innerActionButton.click();
        }
    }
    /**
     * @private
     */
    static get scopedElements() {
        return {
            'hzn-text': HznText,
            'hzn-stack': HznStack,
            'hzn-icon-warning': HznIconWarning,
            'hzn-icon-check': HznIconCheck,
            'hzn-icon-info-outline': HznIconInfoOutline,
            'hzn-icon-close': HznIconClose,
            'hzn-icon-danger': HznIconDanger,
        };
    }
    render() {
        const alertClasses = {
            'hzn-alert': true,
            'heading-only': !this.subHeading && !this.actionText,
            dismissible: Boolean(this.dismissible)
        };
        return html `
      <div role="status" class="${classMap(alertClasses)}">
        <div class="icon">${__classPrivateFieldGet(this, _HznAlert_instances, "m", _HznAlert_renderIconSlot).call(this)}</div>
        <hzn-stack class="alert-message">
          <p class="alert-heading">
            ${this.heading}
          </p>
          ${this.subHeading ? html `<hzn-text class="sub-heading" size="base" tone="subdued"> ${this.subHeading} </hzn-text>` : nothing}
          ${__classPrivateFieldGet(this, _HznAlert_instances, "m", _HznAlert_renderAction).call(this)}
        </hzn-stack>
        ${this.dismissible && this.tone !== 'critical'
            ? html `<button class="dismiss" @click=${__classPrivateFieldGet(this, _HznAlert_instances, "m", _HznAlert_handleDismiss)}>
              <hzn-icon-close></hzn-icon-close>
            </button>`
            : nothing}
      </div>
    `;
    }
}
_HznAlert_emit = new WeakMap(), _HznAlert_instances = new WeakSet(), _HznAlert_handleClick = function _HznAlert_handleClick($event) {
    $event.preventDefault();
    $event.stopImmediatePropagation();
    // emit action event
    __classPrivateFieldGet(this, _HznAlert_emit, "f").call(this, { type: 'action' });
}, _HznAlert_handleDismiss = function _HznAlert_handleDismiss($event) {
    $event.preventDefault();
    $event.stopImmediatePropagation();
    // emit action event
    __classPrivateFieldGet(this, _HznAlert_emit, "f").call(this, {
        type: 'dismiss',
        callback() {
            this.show = false;
        },
    });
}, _HznAlert_renderIconSlot = function _HznAlert_renderIconSlot() {
    if (this.tone === 'critical') {
        return html `<hzn-icon-danger class="alert-icon"></hzn-icon-danger>`;
    }
    else if (this.tone === 'caution') {
        return html `<hzn-icon-warning class="alert-icon"></hzn-icon-warning>`;
    }
    else if (this.tone === 'positive') {
        return html `<hzn-icon-check class="alert-icon"></hzn-icon-check>`;
    }
    else if (this.tone === 'info' || this.tone === 'neutral' || this.tone === 'flagged') {
        return this.hasSlotController.test('leading-icon')
            ? html ` <span class="alert-icon">
            <slot name="leading-icon"></slot>
          </span>`
            : html `<hzn-icon-info-outline class="alert-icon"></hzn-icon-info-outline>`;
    }
}, _HznAlert_renderAction = function _HznAlert_renderAction() {
    const hasSubhead = this.subHeading;
    if (this.actionText) {
        let action;
        switch (this.actionElement) {
            case 'button':
                action = html `<button @click=${__classPrivateFieldGet(this, _HznAlert_instances, "m", _HznAlert_handleClick)} class="action ${hasSubhead ? 'has-subhead' : ''}">${this.actionText}</button>`;
                break;
            case 'a':
                action = html `<a @click=${__classPrivateFieldGet(this, _HznAlert_instances, "m", _HznAlert_handleClick)} class="action ${hasSubhead ? 'has-subhead' : ''}">${this.actionText}</a>`;
                break;
        }
        return action;
    }
    return nothing;
};
HznAlert.styles = [AlertStyles];
__decorate([
    query('.dismiss')
], HznAlert.prototype, "innerDismissButton", void 0);
__decorate([
    query('.action')
], HznAlert.prototype, "innerActionButton", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], HznAlert.prototype, "show", void 0);
__decorate([
    property({ type: String, reflect: true })
], HznAlert.prototype, "tone", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], HznAlert.prototype, "elevated", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], HznAlert.prototype, "dismissible", void 0);
__decorate([
    property({ type: String, reflect: true })
], HznAlert.prototype, "heading", void 0);
__decorate([
    property({ type: String, reflect: true, attribute: 'sub-heading' })
], HznAlert.prototype, "subHeading", void 0);
__decorate([
    property({ type: String, reflect: true, attribute: 'action-text' })
], HznAlert.prototype, "actionText", void 0);
__decorate([
    property({ type: String, reflect: true, attribute: 'action-element' })
], HznAlert.prototype, "actionElement", void 0);
//# sourceMappingURL=alert.js.map